// src/pages/Messages.js
import React, { useState, useEffect, useRef } from 'react';
import { MessageSquare, Bell, Send, User, Paperclip, X, AlertTriangle } from 'lucide-react';
import api from '../api/axios';

const Messages = () => {
  const [activeTab, setActiveTab] = useState('messages');
  const [notifications, setNotifications] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [manager, setManager] = useState(null);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [messageText, setMessageText] = useState('');
  const [chatMessages, setChatMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [chatLoading, setChatLoading] = useState(false);
  const [attachment, setAttachment] = useState(null);
  const [attachmentPreview, setAttachmentPreview] = useState(null);

  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  const roleId = parseInt(localStorage.getItem('role_id')) || 0;
  const isAdmin = [1, 2].includes(roleId);
  const currentUserId = parseInt(localStorage.getItem('user_id')) || null;

  // Fetch initial data
  useEffect(() => {
    fetchNotifications();
    if (isAdmin) fetchEmployees();
    else fetchOwnManager();
  }, [isAdmin]);

  // Auto scroll to bottom of chat
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  const fetchNotifications = async () => {
    try {
      const res = await api.get('/notifications');
      setNotifications(res.data || []);
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
    }
  };

  const fetchEmployees = async () => {
    try {
      const res = await api.get('/users');
      setEmployees(res.data.filter(u => u.role_id === 3) || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchOwnManager = async () => {
    try {
      const userRes = await api.get('/users/me');
      const managerId = userRes.data.manager_id;
      if (managerId) {
        const managerRes = await api.get(`/users/${managerId}`);
        setManager(managerRes.data);
        setSelectedUserId(managerId);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleUserSelect = async (userId) => {
    setSelectedUserId(userId);
    setChatMessages([]);
    if (!userId) return;

    setChatLoading(true);
    try {
      const res = await api.get(`/messages/user/${userId}`);
      setChatMessages(res.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setChatLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      alert("File size must be less than 10MB");
      return;
    }
    setAttachment(file);
    setAttachmentPreview({ name: file.name });
  };

  const removeAttachment = () => {
    setAttachment(null);
    setAttachmentPreview(null);
    fileInputRef.current.value = '';
  };

  const sendMessage = async () => {
    if ((!messageText.trim() && !attachment) || !selectedUserId) return;

    const formData = new FormData();
    formData.append('content', messageText.trim());
    formData.append('recipientId', selectedUserId);
    if (attachment) formData.append('attachment', attachment);

    try {
      await api.post('/messages', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      const newMsg = {
        id: Date.now(),
        sender_id: currentUserId,
        content: messageText.trim(),
        attachment_name: attachment ? attachment.name : null,
        created_at: new Date().toISOString(),
      };

      setChatMessages(prev => [...prev, newMsg]);
      setMessageText('');
      setAttachment(null);
      setAttachmentPreview(null);
    } catch (err) {
      console.error(err);
      alert('Failed to send message');
    }
  };

  const markAllAsRead = async () => {
    try {
      await api.put('/notifications/read-all');
      fetchNotifications();
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      <h1 className="text-3xl font-bold mb-8">Messages & Notifications</h1>

      <div className="bg-white rounded-2xl shadow border border-gray-200 overflow-hidden">
        {/* Tabs */}
        <div className="border-b">
          <nav className="flex">
            <button
              onClick={() => setActiveTab('messages')}
              className={`flex-1 py-5 font-medium flex items-center justify-center gap-2 border-b-4 transition-all ${
                activeTab === 'messages' ? 'border-custom-orange text-custom-orange' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <MessageSquare size={20} />
              Messages
            </button>
            <button
              onClick={() => setActiveTab('notifications')}
              className={`flex-1 py-5 font-medium flex items-center justify-center gap-2 border-b-4 transition-all ${
                activeTab === 'notifications' ? 'border-custom-orange text-custom-orange' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Bell size={20} />
              Notifications
            </button>
          </nav>
        </div>

        {/* Messages Tab */}
        {activeTab === 'messages' && (
          <div className="p-6 md:p-8">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Contact List */}
              <div className="lg:col-span-1 bg-gray-50 p-6 rounded-xl border">
                <h3 className="font-semibold mb-4">Team Contacts</h3>
                {isAdmin ? (
                  <div className="space-y-2 max-h-[500px] overflow-y-auto">
                    {employees.map(u => (
                      <button
                        key={u.id}
                        onClick={() => handleUserSelect(u.id)}
                        className={`w-full p-4 text-left rounded-xl transition-all ${selectedUserId === u.id ? 'bg-custom-orange text-white' : 'hover:bg-white'}`}
                      >
                        {u.first_name} {u.last_name}
                      </button>
                    ))}
                  </div>
                ) : manager ? (
                  <button onClick={() => handleUserSelect(manager.id)} className="w-full p-4 text-left rounded-xl bg-custom-orange text-white">
                    {manager.first_name} {manager.last_name} (Manager)
                  </button>
                ) : (
                  <p className="text-red-600">No manager assigned</p>
                )}
              </div>

              {/* Chat Area */}
              <div className="lg:col-span-2 bg-white rounded-xl border h-[620px] flex flex-col">
                {!selectedUserId ? (
                  <div className="flex-1 flex items-center justify-center text-gray-500">Select a contact to start chatting</div>
                ) : (
                  <>
                    <div className="p-4 border-b font-medium">
                      Chat with {isAdmin ? employees.find(u => u.id === selectedUserId)?.first_name : manager?.first_name}
                    </div>

                    <div className="flex-1 p-4 overflow-y-auto space-y-4">
                      {chatMessages.map(msg => (
                        <div key={msg.id} className={`flex ${msg.sender_id === currentUserId ? 'justify-end' : ''}`}>
                          <div className={`max-w-[75%] px-5 py-3 rounded-2xl ${msg.sender_id === currentUserId ? 'bg-custom-orange text-white' : 'bg-gray-100'}`}>
                            <p>{msg.content}</p>
                          </div>
                        </div>
                      ))}
                      <div ref={messagesEndRef} />
                    </div>

                    <div className="p-4 border-t">
                      <div className="flex gap-3">
                        <input
                          type="text"
                          value={messageText}
                          onChange={e => setMessageText(e.target.value)}
                          onKeyPress={e => e.key === 'Enter' && sendMessage()}
                          placeholder="Type a message..."
                          className="flex-1 border border-gray-300 rounded-2xl px-5 py-4 focus:outline-none focus:border-custom-orange"
                        />
                        <button onClick={sendMessage} disabled={!messageText.trim()} className="bg-custom-orange hover:bg-orange-600 text-white px-8 rounded-2xl">
                          <Send size={22} />
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Notifications Tab */}
        {activeTab === 'notifications' && (
          <div className="p-6 md:p-8">
            {loading ? (
              <div className="text-center py-20">Loading notifications...</div>
            ) : notifications.length === 0 ? (
              <div className="text-center py-20">
                <Bell size={64} className="mx-auto text-gray-300 mb-4" />
                <p className="text-xl font-medium text-gray-600">No notifications yet</p>
                <p className="text-gray-500 mt-2">We'll notify you when something important happens.</p>
              </div>
            ) : (
              <>
                <div className="flex justify-end mb-4">
                  <button
                    onClick={() => alert('Mark all as read feature coming soon')}
                    className="text-custom-orange hover:text-orange-600 text-sm font-medium"
                  >
                    Mark all as read
                  </button>
                </div>

                <div className="max-h-[580px] overflow-y-auto pr-2 space-y-4">
                  {notifications.map((notif) => (
                    <div
                      key={notif.id}
                      className={`p-6 rounded-2xl border transition-all hover:shadow-sm ${!notif.is_read ? 'bg-blue-50 border-blue-100' : 'bg-white'}`}
                    >
                      <div className="flex justify-between items-start">
                        <h4 className="font-semibold text-gray-900">{notif.title}</h4>
                        <span className="text-xs text-gray-500 whitespace-nowrap ml-4">
                          {new Date(notif.created_at).toLocaleString('en-ZA')}
                        </span>
                      </div>
                      <p className="mt-3 text-gray-700 leading-relaxed">{notif.message}</p>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Messages;